import React, { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { TRIGGERS_ENDPOINT } from '../config/api';
import { SHARED_CHART_COLORS } from '../constants/chartColors';
import { getChartCache, setChartCache, CHART_KEYS } from '../utils/cache';
import './TriggersChart.css';

function toProperCase(str) {
  return (str || '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Aggregate by external_stimuli_name: one bar per name, session_count, collect categories.
 * Backend unnests categories so one name can appear multiple times.
 */
function buildBarData(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const aggregated = new Map();
  for (const r of rows) {
    const name = (r.external_stimuli_name || '').trim();
    if (!name || name.toLowerCase() === 'other') continue;
    const nameKey = name.toLowerCase();
    const cat = (r.external_stimuli_category || 'other').trim().toLowerCase() || 'other';
    const count = Number(r.session_count) || 0;

    // Same name can appear across multiple category rows; dedupe by lowercase name,
    // keep the first-seen session_count (backend reports the same count per name) and merge categories.
    const existing = aggregated.get(nameKey);
    if (existing) {
      if (!existing.categories.includes(cat)) existing.categories.push(cat);
    } else {
      aggregated.set(nameKey, { name, sessions: count, categories: [cat] });
    }
  }

  const bars = Array.from(aggregated.values());
  bars.sort((a, b) => b.sessions - a.sessions);
  return bars;
}

/** Sample bar data for new users: same shape as buildBarData output ({ name, sessions, categories }). */
function getSampleTriggersBarData() {
  return [
    { name: 'work deadlines', sessions: 4, categories: ['work'] },
    { name: 'family conflict', sessions: 3, categories: ['relationships'] },
    { name: 'social comparison', sessions: 2, categories: ['social'] },
    { name: 'health worries', sessions: 2, categories: ['health'] },
    { name: 'financial stress', sessions: 1, categories: ['other'] },
  ];
}

const MOBILE_BREAKPOINT = 768;

function TriggersChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsNarrowViewport(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid = user?.userId;
        const cached = uid ? getChartCache(CHART_KEYS.triggers, uid) : null;
        if (!cancelled && cached != null && Array.isArray(cached) && cached.length > 0) {
          setData(cached);
          setLoading(false);
        }
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(TRIGGERS_ENDPOINT, {
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load triggers');
        const json = await res.json();
        const payload = json?.statusCode != null ? JSON.parse(json.body || '[]') : json;
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) {
          setData(list);
          setError(null);
          if (uid) setChartCache(CHART_KEYS.triggers, uid, list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading triggers:', err);
          setError(err.message);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="triggers-chart-container">
        <div className="triggers-chart-loading">Loading triggers...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="triggers-chart-container">
        <div className="triggers-chart-error">Unable to load triggers</div>
      </div>
    );
  }

  const barDataFromApi = buildBarData(data || []);
  const hasRealData = barDataFromApi.length > 0;
  const barData = hasRealData ? barDataFromApi : getSampleTriggersBarData();

  const chartMargin = isNarrowViewport ? { top: 8, right: 24, bottom: 32, left: 16 } : { top: 8, right: 36, bottom: 36, left: 24 };
  const yAxisWidth = isNarrowViewport ? 92 : 140;
  const yAxisTickStyle = isNarrowViewport ? { fontSize: 9, fontWeight: 600 } : { fontSize: 11, fontWeight: 600 };
  const xAxisLabelStyle = isNarrowViewport ? { fontSize: 11, fill: '#111827', fontWeight: 600 } : { fontSize: 14, fill: '#111827', fontWeight: 600 };

  return (
    <div className="triggers-chart-container">
      {!hasRealData && (
        <div className="chart-cta-new-user" role="status">
          <p className="chart-cta-new-user-headline">Start journalling to see your data.</p>
          <p className="chart-cta-new-user-note">This is a sample view.</p>
        </div>
      )}
      <div className="triggers-chart-body">
        <ResponsiveContainer width="100%" height={Math.max(300, barData.length * 36)} minWidth={320}>
          <BarChart
            data={barData}
            layout="vertical"
            margin={chartMargin}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              allowDecimals={false}
              ticks={(() => {
                const maxSessions = Math.max(...barData.map((d) => d.sessions), 1);
                const step = maxSessions <= 5 ? 1 : maxSessions <= 10 ? 2 : Math.ceil(maxSessions / 5);
                const t = [];
                for (let i = 0; i <= maxSessions; i += step) t.push(i);
                if (t[t.length - 1] !== maxSessions) t.push(maxSessions);
                return t;
              })()}
              tick={false}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickLine={false}
              label={{
                value: 'Number of sessions',
                position: 'bottom',
                offset: -6,
                style: xAxisLabelStyle,
              }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={yAxisWidth}
              tick={yAxisTickStyle}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickLine={false}
              tickFormatter={(v) => toProperCase(v)}
            />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const d = payload[0].payload;
                const categoryLabel = d.categories.map((c) => toProperCase(c)).join(', ');
                return (
                  <div className="triggers-chart-tooltip">
                    <div className="triggers-chart-tooltip-label">{toProperCase(d.name)}</div>
                    <div className="triggers-chart-tooltip-meta">{categoryLabel}</div>
                    <div className="triggers-chart-tooltip-value">
                      {d.sessions} session{d.sessions === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="sessions" radius={[0, 4, 4, 0]} maxBarSize={28}>
              <LabelList
                dataKey="sessions"
                position="right"
                offset={6}
                style={{ fontSize: 11, fill: '#6b7280' }}
              />
              {barData.map((d, i) => (
                <Cell key={i} fill={SHARED_CHART_COLORS[i % SHARED_CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TriggersChart;
